/**
 * Proleptic Gregorian calendar days at 00:00 UTC, as the contract (and the Swift runner's
 * CalcContext calendar) defines them. A `Day` is the number of days since 1970-01-01, so
 * ordering and day differences are plain number arithmetic.
 */

export type Day = number & { readonly __brand: 'Day' };

export type CalendarUnit = 'day' | 'week' | 'month' | 'year';

export interface YearMonthDay {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

const asDay = (value: number): Day => value as Day;

/** Days from 1970-01-01 to a civil date (Howard Hinnant's days_from_civil). */
export function fromYmd(year: number, month: number, day: number): Day {
  const y = month <= 2 ? year - 1 : year;
  const era = Math.floor(y / 400);
  const yoe = y - era * 400;
  const doy = Math.floor((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + day - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return asDay(era * 146097 + doe - 719468);
}

export function toYmd(value: Day): YearMonthDay {
  const z = value + 719468;
  const era = Math.floor(z / 146097);
  const doe = z - era * 146097;
  const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const day = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const month = mp < 10 ? mp + 3 : mp - 9;
  return { year: yoe + era * 400 + (month <= 2 ? 1 : 0), month, day };
}

export const isLeapYear = (year: number): boolean => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const MONTH_LENGTHS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

export const daysInMonth = (year: number, month: number): number =>
  month === 2 && isLeapYear(year) ? 29 : MONTH_LENGTHS[month - 1]!;

const DATE_TEXT = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Parse "YYYY-MM-DD", rejecting impossible days (as the Swift codec does). */
export function parseDay(text: string): Day {
  const match = DATE_TEXT.exec(text);
  if (!match) throw new Error(`expected YYYY-MM-DD date, got "${text}"`);
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
    throw new Error(`expected YYYY-MM-DD date, got "${text}"`);
  }
  return fromYmd(year, month, day);
}

export function formatDay(value: Day): string {
  const { year, month, day } = toYmd(value);
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export const yearOf = (value: Day): number => toYmd(value).year;
export const monthOf = (value: Day): number => toYmd(value).month;
export const dayOfMonth = (value: Day): number => toYmd(value).day;
export const daysInMonthOf = (value: Day): number => {
  const { year, month } = toYmd(value);
  return daysInMonth(year, month);
};

/** Absolute month index `12·year + month`, for calendar-month (day-ignoring) arithmetic. */
export const absoluteMonth = (value: Day): number => {
  const { year, month } = toYmd(value);
  return year * 12 + month;
};

// MARK: Adding

export const addDays = (value: Day, days: number): Day => asDay(value + days);
export const addWeeks = (value: Day, weeks: number): Day => asDay(value + 7 * weeks);

/**
 * `date(byAdding: .month)`: move year/month by `months` in one step and clamp the day to the
 * target month's length. Foundation quirk (reproduced): stepping back into February of an
 * earlier year sizes that February by the leap-ness of year `2·from − to − 2`, so
 * 2048-09-29 − 31 months = 2046-03-01 and 1990-01-29 − 23 months = 1988-02-28.
 */
export function addMonths(value: Day, months: number): Day {
  const { year, month, day } = toYmd(value);
  const index = year * 12 + (month - 1) + months;
  const targetYear = Math.floor(index / 12);
  const targetMonth = index - targetYear * 12 + 1;
  const length =
    targetMonth === 2 && targetYear < year
      ? isLeapYear(2 * year - targetYear - 2) ? 29 : 28
      : daysInMonth(targetYear, targetMonth);
  return fromYmd(targetYear, targetMonth, Math.min(day, length));
}

/** `date(byAdding: .year)`: clamps 29 Feb to 28 Feb in non-leap years (no February quirk). */
export function addYears(value: Day, years: number): Day {
  const { year, month, day } = toYmd(value);
  const targetYear = year + years;
  return fromYmd(targetYear, month, Math.min(day, daysInMonth(targetYear, month)));
}

/** `calendar.date(byAdding: unit, value: count, to:)` (weeks are `.weekOfYear`: 7 days). */
export function addUnits(value: Day, unit: CalendarUnit, count: number): Day {
  switch (unit) {
    case 'day':
      return addDays(value, count);
    case 'week':
      return addWeeks(value, count);
    case 'month':
      return addMonths(value, count);
    case 'year':
      return addYears(value, count);
  }
}

export function startOfMonth(value: Day): Day {
  const { year, month } = toYmd(value);
  return fromYmd(year, month, 1);
}

// MARK: Differences (`calendar.dateComponents([unit], from:to:)`)

export const daysBetween = (from: Day, to: Day): number => to - from;

/** Whole weeks, truncated toward zero. */
export const weeksBetween = (from: Day, to: Day): number => Math.trunc((to - from) / 7);

/**
 * Whole calendar months from `from`, capped at month end: for `to ≥ from` the largest n
 * with `addMonths(from, n) ≤ to`; otherwise `-(largest n with addMonths(from, -n) ≥ to)`.
 * 2024-01-31 → 2024-02-28 is 0 months, → 2024-02-29 is 1. Backwards differences inherit
 * addMonths' February quirk, which can reach one month past the calendar estimate.
 */
export const monthsBetween = (from: Day, to: Day): number => steppedDifference(from, to, 1, addMonths);

/** Whole years, defined like `monthsBetween` with year steps (2024-02-29 → 2025-02-28 is 1). */
export const yearsBetween = (from: Day, to: Day): number =>
  steppedDifference(from, to, 12, (day, months) => addYears(day, months / 12));

function steppedDifference(from: Day, to: Day, monthsPerStep: number, step: (day: Day, months: number) => Day): number {
  const estimate = Math.trunc((absoluteMonth(to) - absoluteMonth(from)) / monthsPerStep);
  if (to >= from) {
    let n = Math.max(0, estimate);
    while (n > 0 && step(from, n * monthsPerStep) > to) n -= 1;
    return n;
  }
  let n = Math.max(0, -estimate) + 1;
  while (n > 0 && step(from, -n * monthsPerStep) < to) n -= 1;
  return -n;
}

export function unitsBetween(from: Day, to: Day, unit: CalendarUnit): number {
  switch (unit) {
    case 'day':
      return daysBetween(from, to);
    case 'week':
      return weeksBetween(from, to);
    case 'month':
      return monthsBetween(from, to);
    case 'year':
      return yearsBetween(from, to);
  }
}

export const minDay = (a: Day, b: Day): Day => (a <= b ? a : b);
export const maxDay = (a: Day, b: Day): Day => (a >= b ? a : b);
